import React from 'react';
import { useTranslation } from 'react-i18next';

import { CitationsSection } from '~/components/citations-section/CitationsSection';
import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '~/components/text-section/TextSection';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ImpactedCountriesAndRegions } from '~/modules/impacted-countries-and-regions/ImpactedCountriesAndRegions';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { useCatalystInsightDetails } from '~/react-query-hooks/insights/useCatalystInsights';
import { CatalystDrawerHeader } from '~/widgets/catalyst-insights-drawer/catalyst-drawer-header/CatalystDrawerHeader';

import * as S from './MarketCatalystWidget.styles';

export interface MarketCatalystWidgetProps {
  insightId: string;
}

export const MarketCatalystWidget = ({
  insightId,
}: MarketCatalystWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const {
    data: catalystInsight,
    isLoading,
    error,
  } = useCatalystInsightDetails(insightId, !!insightId);

  if (error) {
    throw error;
  }

  if (isLoading) {
    return <S.StyledLoader />;
  }

  if (!catalystInsight) {
    return null;
  }

  return (
    <ErrorBoundaryWrapper>
      <ErrorBoundaryWrapper>
        <CatalystDrawerHeader
          identifier={catalystInsight.identifier}
          catalystInsight={catalystInsight}
        />
      </ErrorBoundaryWrapper>
      <S.SectionGroupsWrapper>
        {catalystInsight.section_groups.map((group, gi) => (
          <S.GroupWrapper
            key={`${group.title}-${gi}`}
            data-testid="section-group-wrapper"
          >
            <S.SectionGroupTitle>{group.title}</S.SectionGroupTitle>
            {group.sections.map((section, si) => (
              <TextSection
                key={`${section.title}-${si}`}
                title={section.title}
                textContent={section.content ?? []}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </S.GroupWrapper>
        ))}
        <div>
          <ThematicFocus
            themes={catalystInsight.themes}
            otherThemes={catalystInsight.other_themes}
            title={t('insights:catalyst.thematicFocus')}
            withPill
          />
        </div>
        <ImpactedCountriesAndRegions
          impactedCountries={catalystInsight.impacted_countries}
          impactedRegions={catalystInsight.impacted_regions}
          title={t('insights:catalyst.mostImpactedRegions')}
        />
        <ImpactedCompanies
          impactedCompanies={catalystInsight.impacted_companies}
          variant="secondary"
          title={t('insights:catalyst.mostImpactedCompanies')}
          insightCreatedAt={catalystInsight.created_at}
        />
        {!!catalystInsight.citations.length && (
          <S.CitationsSectionWrapper>
            <S.CitationsTitle>
              {t('insights:catalyst.citations')}
            </S.CitationsTitle>
            <CitationsSection
              count={catalystInsight.citations.length}
              items={catalystInsight.citations}
              title={t('insights:catalyst.webLinks')}
            />
          </S.CitationsSectionWrapper>
        )}
      </S.SectionGroupsWrapper>
    </ErrorBoundaryWrapper>
  );
};
